<script>
   /**************************************************************/
   /* DataLoader for PLS1-DA app                                 */
   /* -------------------------                                  */
   /* Loads test set from CSV file and returns its main output.   */
   /* If PLS model is provided it also applies the model and     */
   /* returns object with PLS results. In this case it will      */
   /* also recalculate the result object if array with outliers  */
   /* has been changed                                           */
   /**************************************************************/


   import { FileSelector, Switch, Spinner, Select, Container, Button, TextInput } from 'svelte-controls-basic';
   import { addDummy, parseCSV, app_pls_predict, parseCSVParams, labelsByWidth, readSPCFiles }
      from '@mdatools-apps/shared/utils';
   import DatasetWidget from '@mdatools-apps/shared/widgets/DatasetWidget.svelte';


   let {
      WASM,                             // WASM object, required with 'model': 'predict' calls it
      model = null,                     // object with PLS model (optional)
      outliers = [],                    // array with outlier indices (optional)
      relabeled = [],                   // array with indices of relabeled objects (optional)
      classMandatory = false,           // logical, if class column is mandatory in the data file
      classNames = null,                // class names of the model the data is loaded for (optional)
      title = '',                       // title, to show on top of the block
      dataset = $bindable(),            // JSON with datasets
      resobj = $bindable(),             // JSON with PLS results (if model is provided)
   } = $props();

   // prefix for the label ids the controls below are named by. it must be unique on the page and
   // every tab of the app stays mounted, so it can not be written out by hand
   const uid = $props.id();

   // possible delimiters and header options
   const sepOptions = [',', ';'];
   const headerOptions = $derived(model ? ['no', 'yes'] : ['no', 'labels', 'values']);

   // data properties
   let dataFile = $state();                     // CSV file
   let varvaluesName = $state('');              // name of the variable values to be used as header
   let varvaluesUnits = $state('');             // units of the variable values
   // handed to the parser, never a control here: the requirement where a class column is
   // mandatory, otherwise the guess of 'guessParams'
   // svelte-ignore state_referenced_locally
   let hasClassLabels = classMandatory;

   // data properties, that will be autodetected from CSV file
   let sep = $state(',');                       // separator between values
   let header = $state('no');                   // first row contains header (labels or values)
   let hasLabels = $state(false);               // first column contains row labels


   // promise, app status for handling and showing errors
   let promise = $state(null);
   let status = $state('neutral');
   let errMsg = $state('');

   // method for showing error. it takes a string or an Error: the box printed the Error object,
   // which reads 'Error: ...'
   function setError(msg) {
      status = 'error';
      errMsg = msg instanceof Error ? msg.message : String(msg);
      console.error(msg)
   }

   // method for setting neutral state. a step which succeeds sets it too: there used to be a
   // 'success' status as well, and nothing told the two apart
   function setNeutral() {
      status = 'neutral';
      errMsg = '';
   }


   // every prediction is stamped with the run which asked for it, and only the newest run may
   // write; a run is retired by a newer one and by the component going away. the C call itself
   // blocks the page, so the window is the module instantiation, which yields: a cancel of the
   // model pressed in it unmounts the component, and the result still landed in the session
   // through the binding, for a model that was gone. its own counter, not 'guessRun' below: a
   // guess and a prediction are unrelated, so neither may retire the other. plain 'let', never
   // '$state' (see 'PrepWidget')
   let predictRun = 0;
   $effect(() => () => { predictRun++; });

   // method to apply PLS model
   function predict(model, dataset, outliers) {

      // if result object is already defined do nothing
      if (resobj) return;
      const myRun = ++predictRun;
      promise = WASM()
         .then(Module => {
            if (myRun !== predictRun) return;
            resobj = app_pls_predict(Module, model, dataset, outliers, true, relabeled);
            setNeutral();
         })
         .catch(e =>{
            if (myRun !== predictRun) return;
            resobj = null;
            setError(e);
         });
   };

   // a guess is read asynchronously and must not land after the file was changed, cleared or
   // loaded: every run of the 'dataFile' effect below takes the next number, and a read that
   // finishes with an older one is dropped
   let guessRun = 0;
   // set when the width rule in 'guessParams' turned the row-label switch on, until the next guess
   let labelsFromWidth = false;
   // the component going away retires a read in flight as well: an spc set landing after that
   // was written through the binding all the same
   $effect(() => () => { guessRun++; });

   // method for guessing CSV parameters
   function guessParams(dataFile, model) {

      // a label column taken from the last file's width must not cut the first variable off this
      // one, whether its guess fails or never runs; a row-label flag the text guess set stays
      if (labelsFromWidth) hasLabels = false;
      labelsFromWidth = false;

      // no data file is provided - no guessing
      if (!dataFile || dataFile.size < 10) return;

      // read first 100Kb
      const chunkSize = 100000;
      const first100KB = dataFile.slice(0, chunkSize);
      const myRun = guessRun;

      const reader = new FileReader();
      reader.onload = () => {
         if (myRun !== guessRun) return;
         const csvString = reader.result;
         if (!csvString || csvString.length === 0) return;

         // 'parseCSVParams' throws when it can not make sense of the chunk: too few rows, no
         // delimiter it detects, or a value it can not read as a number. this runs inside a
         // FileReader callback, so an error thrown here escapes into the event loop instead of
         // any caller, and without the catch the component said nothing about why the guess
         // did not happen. the chunk can be the reason on its own: a file with very wide rows
         // has its last sampled row cut at the boundary, so the message names the chunk and
         // does not repeat the parser's sentence, which speaks of the whole file.
         try {
            let guessed, ncolsTotal;
            [sep, hasLabels, guessed, header, ncolsTotal] = parseCSVParams(csvString);
            // a train or test set has a class column; the row-label switch places it
            hasClassLabels = classMandatory || guessed;
            // numeric row labels the text guess missed, assumed from a row one column wider than
            // the model needs; not when the guess put the class in column 0
            if (model && !hasLabels && !guessed &&
                  labelsByWidth(ncolsTotal, dataCols(), hasClassLabels)) {
               hasLabels = true;
               labelsFromWidth = true;
            }
            if (model && header !== 'no') header = 'yes';
            setNeutral();
         } catch (e) {
            // the class flag has no switch here, so the last file's must not stay for this one
            hasClassLabels = classMandatory;
            console.error(e);
            setError('Could not guess the CSV parameters from the first 100 KB of the file. ' +
               'Check the delimiter, header and row label settings and try to load it.');
         }
      };

      reader.onerror = () => {
         if (myRun !== guessRun) return;
         // the handler's argument is a ProgressEvent, which used to be printed as '[object
         // ProgressEvent]'; the reason is on the reader
         const reason = reader.error ? reader.error.message : 'unknown error';
         setError('Error reading CSV file. ' + reason);
      };

      reader.readAsText(first100KB);
   }

   // the number of predictors a set must have for the model, read by the guess and the check. the
   // check adds the responses 'addDummy' makes of the class column
   function dataCols() {
      return (model.prep && model.prep.npred) ? model.prep.npred :
         model.npred + model.exclvars.length;
   }

   // checks a parsed set against the model and the settings it was loaded with, and takes it.
   // the spc path shares it: its set used to be taken with none of the checks, the width included
   function acceptData(d) {
      try {
         // check that the number of rows is sufficient
         if (d.values.nrows < 1 ) {
            throw new Error('Dataset must have at least 1 row.');
         }

         // check that number of variables is sufficient
         if ((d.values.ncols - d.nresp) < 2) {
            throw new Error('Dataset should have at least two predictors.');
         }

         if (classMandatory) {
            if (!d.classNames || d.classNames.length !== 2) {
               throw new Error('Dataset should have two classes.');
            }

            // 'predict' keeps the totals of a result under 'total', next to the classes, and the
            // FoM plot lists the class names next to a 'total' option
            if (d.classNames.includes('total')) {
               throw new Error("The class name 'total' is taken: the results keep their totals " +
                  'under it. Rename the class in your file.');
            }

            // the model's class names are the keys of everything the results are looked up by,
            // and the first of them is the class the dummy marks with +1. a file which spells
            // them differently is refused, and one which lists them in the other order - the
            // first row decides that - takes the model's order, so its reference values agree
            // with the training set's
            if (classNames) {
               const same = classNames.length === d.classNames.length &&
                  classNames.every(v => d.classNames.includes(v));
               if (!same) {
                  throw new Error('Dataset has classes "' + d.classNames.join('", "') +
                     '" while the model was trained for "' + classNames.join('", "') + '".');
               }
               d.classNames = classNames.slice();
            }
            d = addDummy(d);
         } else if (d.classNames && d.classNames.length > 0) {
            throw new Error('New data should not contain class labels.');
         }


         // if model is available inherit variable properties from the model
         if (model) {

            // check that number of variables match
            const ncolsExpected = classMandatory ? dataCols() + model.nresp : dataCols();

            if (d.values.ncols !== ncolsExpected) {
               throw new Error('Dataset has different number of columns than the training set.');
            }

            // if data values in the training set were in reverse order it is expected
            // that they are in the reverse order in this dataset as well
            d.varrev = model.varrev;
            d.header = model.header;
            d.varlabels = model.varlabels;
            d.varvalues = model.varvalues;
            d.varindices = model.varindices;
            d.varvaluesName = model.varvaluesName;
            d.varvaluesUnits = model.varvaluesUnits;
         }

         dataset = d;
         setNeutral();
      } catch (e) {
         // no 'dataset = null' here: the button and the spc read which reach this run only while
         // no data set is loaded, so the write cleared nothing and only turned the session's
         // initial 0 into null. that counted as a change for the 'dataFile' effect, which re-ran
         // for the same file and wiped this message with its 'setNeutral' before it was ever
         // shown, so the first failed load said nothing and only the second did
         setError(e);
      }
   }

   // method for parsing CSV content provided as one large string to dataset object.
   function setData(strData) {
      try{

         // parsed without a response: the class column becomes the response below, as a dummy
         // variable, once the classes have been checked
         let d = parseCSV(
            strData,
            sep,
            header === 'yes' ? 'labels' : header, // the prediction tabs offer 'yes' for labels
            hasLabels,
            hasClassLabels,
            varvaluesName,
            varvaluesUnits,
            0
         );

         // replicate labels on top look like a class column to the guess: a set whose class column
         // is not the model's classes is parsed again as row labels and predictors, if that parses
         if (!classMandatory && hasClassLabels && classNames &&
               !d.classNames.every(c => classNames.includes(c))) {
            try {
               d = parseCSV(strData, sep, header === 'yes' ? 'labels' : header, true, false,
                  varvaluesName, varvaluesUnits, 0);
            } catch (e) {}
         }
         d['fileName'] = dataFile.name.replace(/\.[^/.]+$/, "");
         acceptData(d);
      } catch (e) {
         // no 'dataset = null' here either, see 'acceptData'
         setError(e);
      }
   }

   // a file is a csv when its name says so. the browser fills 'type' from extension mappings of
   // its own or of the platform, and firefox up to esr 140 takes the platform's for '.csv',
   // where any installed application may have registered the extension under a type of its
   // choosing; the selector next to this already accepts files by their extension. an Array
   // and the empty string have no 'name'
   const isCSVFile = f => !!f && typeof f.name === 'string' && f.name.toLowerCase().endsWith('.csv');

   // a read in progress: the selector and the settings give way to a spinner until the data
   // lands or the read fails, and both of those set the status again
   function setLoading() {
      status = 'loading';
      errMsg = '';
   }

   // method for reading the CSV file
   function readCSV () {
      setLoading();
      // a guess still in flight is retired: it would land during the read and write the
      // delimiter, or the status, over it
      guessRun++;
      const reader = new FileReader();
      reader.onload = () => { setData(reader.result) };
      // without this a file which can not be read would leave the spinner up for good
      reader.onerror = () => {
         const reason = reader.error ? reader.error.message : 'unknown error';
         setError('Error reading CSV file. ' + reason);
      };
      reader.readAsText(dataFile);
   }

   // method for reading the SPC file. the read is retired like a guess, by the next run of the
   // 'dataFile' effect and by the teardown of 'guessRun': its set used to land after the file
   // had been changed or cleared, or after the component had gone
   async function readSPC () {
      setLoading();
      const myRun = guessRun;
      try {
         // several files arrive as a plain Array, from the dialog and from a drop alike since
         // svelte-controls-basic 3.2.0; only a single File has to be wrapped
         const dataFiles = Array.isArray(dataFile) ? dataFile : [dataFile];
         const d = await readSPCFiles(dataFiles);
         if (myRun !== guessRun) return;
         acceptData(d);
      } catch (e) {
         if (myRun !== guessRun) return;
         setError('Failed to read SPC file: ' + (e instanceof Error ? e.message : String(e)));
      }
   }

   // reset whole component to initial state of the component
   function reset () {
      // retires a prediction in flight: the effect sees no data and starts none
      predictRun++;
      dataFile = '';
      dataset = null;
      resobj = null;
      varvaluesName = '';
      varvaluesUnits = '';
      setNeutral();
   }

   // if data file is selected - guess parameters
   $effect(() => {
      guessRun++;

      // undefined is the initial value, before the selector wrote anything; the 0 that used to
      // be tested here as well is a value it never holds
      if (dataFile === undefined || dataset) return;

      // if data file is empty reset status and error messages
      if (dataFile === null || dataFile === '') {
         reset();
         return;
      }

      // dataFile holds several files: a plain Array, from the dialog and from a drop alike
      if (Array.isArray(dataFile)) {

         // 'v' is a File: 'v.slice()' is Blob.slice and returns a Blob, never the extension, so
         // the name has to be taken explicitly. the selector accepts '.SPC' as well
         if (!dataFile.every(v => v.name.toLowerCase().endsWith('.spc'))) {
            setError('Multiple file selection is allowed only for SPC files.');
            return;
         }

         readSPC();
         return;
      }

      if (isCSVFile(dataFile)) {
         guessParams(dataFile, model);
         setNeutral();
         return;
      }

      // spc files carry no class labels, so a dataset which needs them can not come from one
      if (classMandatory) {
         setError('The file has to be a CSV file: this dataset needs class labels, ' +
            'which only a CSV file can hold.');
         return;
      }

      readSPC();
   });

   // if separator was changed remove error message
   $effect(() => {
      if (sep) {
         setNeutral();
      }
   });

   // if dataset, model or array with outliers have been changed, make new predictions. the list
   // lengths are read before the condition as in pls, where an undo writes null over a result
   // which is null already and nothing else changes. here every handler which touches a list also
   // replaces the result with a fresh object, which the binding makes this effect watch, so the
   // reads change nothing today; they keep that independent of the tabs. the error message used
   // to be a condition as well, against a retry, and it is what held the effect after a failed
   // prediction: the handlers replaced the result, but the message stood, so the undo and reset
   // buttons were dead. it is not needed: a failure writes null over the result, which runs this
   // once more only while the result is still the session's initial 0, and changes nothing else
   // read here - the data, the model, the result and the two list lengths
   $effect(() => {
      outliers.length;
      relabeled.length;
      if (dataset && model && !resobj) {
         predict(model, dataset, outliers);
      }
   });
</script>

{#if status === 'loading'}

   <!-- the file is read asynchronously, so the spinner is in the DOM before the parse, which
        runs on the main thread. whether a frame is painted in between is the browser's call -->
   <Container>
      <Spinner />
      <p>Loading data...</p>
   </Container>

{:else if !dataset }

   <!-- show if data is not loaded -->
   <Container>
      {#if classMandatory}
      <FileSelector
         message="Select CSV file with dataset"
         bind:file={dataFile}
      />
      {:else}
      <FileSelector
         message="Select CSV or SPC file with dataset"
         acceptType=".csv, .spc"
         multiple={true}
         bind:file={dataFile}
      />
      {/if}
   </Container>

   <!-- show if file is selected but data is not loaded -->
   {#if isCSVFile(dataFile)}
   <Container label="Delimiter" id="{uid}-sep">
      <Select bind:value={sep} options={sepOptions} />
   </Container>
   <Container label="Row labels" id="{uid}-haslabels">
      <Switch bind:value={hasLabels} options={['no', 'yes']} />
   </Container>
   <Container label="Header" id="{uid}-header">
      <Select bind:value={header} options={headerOptions} />
   </Container>
   {#if header==='values'}
   <Container label="Header name (predictors)" id="{uid}-varvaluesname">
      <TextInput bind:value={varvaluesName} placeholder="e.g. Wavelength" />
   </Container>
   <Container label="Header units (predictors)" id="{uid}-varvaluesunits">
      <TextInput bind:value={varvaluesUnits} maxLength={10} placeholder="e.g. nm" />
   </Container>
   {/if}
   <Container label="&nbsp;">
      <Button text='Load' onclick={readCSV}/>
   </Container>
   {/if}

{:else}


   {#if promise}

      <!-- calculation is in process. 'predict' ends in a catch of its own which reports through
           'setError', so the promise never rejects and a catch block here would never show -->
      {#await promise}
         <Container>
            <Spinner />
            <p>{model ? 'Loading data and apply model...' : 'Loading data...'}</p>
         </Container>
      {/await}

   {/if}

{/if}

{#if dataset }
   <!-- dataset is loaded - show info and cancel button -->
   <DatasetWidget {dataset} {reset} {title} />
{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}

